import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { NoteParams } from "../shared/note.types";
import { ticketService } from "./ticket.service";
import {
	normalizeTicketTypesQueryOptions,
	type SubContractorParams,
	type TicketAssetAssociationParams,
	type TicketAssignmentParams,
	type TicketFileParams,
	type TicketParams,
	type TicketRecordAssociationParams,
	type TicketTypesListParams,
	type TicketTypesQueryOptionsInput,
} from "./ticket.types";

// ==================== QUERY KEYS ====================

export const ticketKeys = {
	// Base keys
	all: ["ticket"] as const,

	// Tickets
	tickets: () => [...ticketKeys.all, "tickets"] as const,
	ticketsList: (start: number, count: number, filters?: TicketParams) =>
		[...ticketKeys.tickets(), "list", start, count, filters] as const,
	ticketsDetail: (id: string, organizationId?: string) =>
		[...ticketKeys.tickets(), "detail", id, organizationId] as const,
	ticketsCount: (params?: TicketParams) =>
		[...ticketKeys.tickets(), "count", params] as const,
	ticketsValidStatuses: (id: string, organizationId?: string) =>
		[...ticketKeys.tickets(), id, "valid-statuses", organizationId] as const,
	ticketsTypes: (organizationId?: string, subOrganizationId?: string) =>
		[
			...ticketKeys.tickets(),
			"types",
			organizationId ?? null,
			subOrganizationId ?? null,
		] as const,
	ticketTypesCount: (params?: TicketTypesListParams) =>
		[...ticketKeys.tickets(), "ticket-types", "count", params] as const,
	ticketTypesPage: (
		start: number,
		count: number,
		params?: TicketTypesListParams,
	) =>
		[
			...ticketKeys.tickets(),
			"ticket-types",
			"page",
			start,
			count,
			params,
		] as const,
	ticketTypesAll: (params?: TicketTypesListParams) =>
		[...ticketKeys.tickets(), "ticket-types", "all", params] as const,
	ticketTypeDetail: (
		ticketTypeId: string,
		subOrganizationId?: string,
		organizationId?: string,
	) =>
		[
			...ticketKeys.tickets(),
			"ticket-type-detail",
			ticketTypeId,
			subOrganizationId ?? "",
			organizationId ?? "",
		] as const,
	ticketsPriorities: (organizationId?: string) =>
		[...ticketKeys.tickets(), "priorities", organizationId ?? null] as const,
	geometrySources: () => [...ticketKeys.tickets(), "geometry-sources"] as const,
	gpsMethods: () => [...ticketKeys.tickets(), "gps-methods"] as const,
	ticketsFeatures: (start: number, count: number, filters?: TicketParams) =>
		[...ticketKeys.tickets(), "features", start, count, filters] as const,

	// Ticket Assignments
	ticketAssignments: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "assignments"] as const,
	ticketAssignmentsCount: (ticketId: string, params?: TicketAssignmentParams) =>
		[...ticketKeys.ticketAssignments(ticketId), "count", params] as const,
	ticketAssignmentsList: (
		ticketId: string,
		start: number,
		count: number,
		params?: TicketAssignmentParams,
	) =>
		[
			...ticketKeys.ticketAssignments(ticketId),
			"list",
			start,
			count,
			params,
		] as const,

	// Ticket Sub-Contractors
	ticketSubContractors: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "sub-contractors"] as const,
	ticketSubContractorsCount: (ticketId: string, params?: SubContractorParams) =>
		[...ticketKeys.ticketSubContractors(ticketId), "count", params] as const,
	ticketSubContractorsList: (
		ticketId: string,
		start: number,
		count: number,
		params?: SubContractorParams,
	) =>
		[
			...ticketKeys.ticketSubContractors(ticketId),
			"list",
			start,
			count,
			params,
		] as const,

	// Ticket Type History
	ticketTypeHistory: (ticketTypeId: string) =>
		[...ticketKeys.tickets(), "ticket-type-history", ticketTypeId] as const,
	ticketTypeHistoryCount: (ticketTypeId: string) =>
		[...ticketKeys.ticketTypeHistory(ticketTypeId), "count"] as const,
	ticketTypeHistoryList: (ticketTypeId: string, start: number, count: number) =>
		[
			...ticketKeys.ticketTypeHistory(ticketTypeId),
			"list",
			start,
			count,
		] as const,

	// Ticket History
	ticketHistory: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "history"] as const,
	ticketHistoryCount: (ticketId: string) =>
		[...ticketKeys.ticketHistory(ticketId), "count"] as const,
	ticketHistoryList: (ticketId: string, start: number, count: number) =>
		[...ticketKeys.ticketHistory(ticketId), "list", start, count] as const,

	// Ticket asset associations
	ticketAssets: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "assets"] as const,
	ticketAssetsCount: (
		ticketId: string,
		params?: TicketAssetAssociationParams,
	) => [...ticketKeys.ticketAssets(ticketId), "count", params] as const,
	ticketAssetsList: (
		ticketId: string,
		start: number,
		count: number,
		params?: TicketAssetAssociationParams,
	) =>
		[
			...ticketKeys.ticketAssets(ticketId),
			"list",
			start,
			count,
			params,
		] as const,

	// Ticket record associations
	ticketRecords: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "records"] as const,
	ticketRecordsCount: (
		ticketId: string,
		params?: TicketRecordAssociationParams,
	) => [...ticketKeys.ticketRecords(ticketId), "count", params] as const,
	ticketRecordsList: (
		ticketId: string,
		start: number,
		count: number,
		params?: TicketRecordAssociationParams,
	) =>
		[
			...ticketKeys.ticketRecords(ticketId),
			"list",
			start,
			count,
			params,
		] as const,

	// Ticket files
	ticketFiles: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "files"] as const,
	ticketFilesCount: (ticketId: string, params?: TicketFileParams) =>
		[...ticketKeys.ticketFiles(ticketId), "count", params] as const,
	ticketFilesList: (
		ticketId: string,
		start: number,
		count: number,
		params?: TicketFileParams,
	) =>
		[
			...ticketKeys.ticketFiles(ticketId),
			"list",
			start,
			count,
			params,
		] as const,
	ticketFileContent: (ticketId: string, fileId: string) =>
		[...ticketKeys.ticketFiles(ticketId), "content", fileId] as const,
	ticketFileThumbnail: (ticketId: string, fileId: string) =>
		[...ticketKeys.ticketFiles(ticketId), "thumbnail", fileId] as const,

	// Ticket notes
	ticketNotes: (ticketId: string) =>
		[...ticketKeys.tickets(), ticketId, "notes"] as const,
	ticketNotesCount: (ticketId: string, params?: NoteParams) =>
		[...ticketKeys.ticketNotes(ticketId), "count", params] as const,
	ticketNotesList: (
		ticketId: string,
		start: number,
		count: number,
		params?: NoteParams,
	) =>
		[
			...ticketKeys.ticketNotes(ticketId),
			"list",
			start,
			count,
			params,
		] as const,
} as const;

function ticketTypesAllQueryOptions(options?: TicketTypesQueryOptionsInput) {
	const params = normalizeTicketTypesQueryOptions(options);
	return queryOptions({
		queryKey: ticketKeys.ticketTypesAll(params),
		queryFn: () => ticketService.getTicketTypesPaged(options),
		staleTime: 10 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
}

/** Org-enabled ticket types only — Create Ticket modal / type dropdown (not org-admin tables). */
function ticketTypesAvailableForCreateQueryOptions(
	organizationId: string,
	subOrganizationId?: string,
) {
	return queryOptions({
		queryKey: ticketKeys.ticketsTypes(organizationId, subOrganizationId),
		queryFn: () =>
			ticketService.getTicketTypes({ organizationId, subOrganizationId }),
		staleTime: 0,
		gcTime: 5 * 60 * 1000,
	});
}

// ==================== TICKET QUERY OPTIONS ====================

export const ticketQueryOptions = {
	/**
	 * Get tickets with pagination and optional filtering
	 */
	list: (start: number = 1, count: number = 10, params?: TicketParams) =>
		queryOptions({
			queryKey: ticketKeys.ticketsList(start, count, params),
			queryFn: () => ticketService.getTickets(start, count, params),
			staleTime: 0,
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get tickets count with optional filtering
	 */
	count: (params?: TicketParams) =>
		queryOptions({
			queryKey: ticketKeys.ticketsCount(params),
			queryFn: ({ signal }) =>
				ticketService.getTicketsCount(params, { signal }),
			staleTime: 0, // no stale time
			gcTime: 0, // 0
		}),

	/**
	 * Get a single ticket by ID
	 */
	detail: (id: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: ticketKeys.ticketsDetail(id, options?.organizationId),
			queryFn: () => ticketService.getTicket(id, options),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!id && id.length > 0,
		}),

	/**
	 * Get valid statuses for a ticket
	 */
	validStatuses: (id: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: ticketKeys.ticketsValidStatuses(id, options?.organizationId),
			queryFn: () => ticketService.getTicketValidStatuses(id, options),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!id && id.length > 0,
		}),

	/**
	 * Get ticket types (for dropdowns, etc.)
	 * Uses count + paged endpoints to handle > 100 types.
	 */
	types: (options?: TicketTypesQueryOptionsInput) => {
		const params = normalizeTicketTypesQueryOptions(options);
		return queryOptions({
			queryKey: [...ticketKeys.ticketsTypes(), params] as const,
			queryFn: () => ticketService.getTicketTypesPaged(options),
			staleTime: 10 * 60 * 1000,
			gcTime: 30 * 60 * 1000,
		});
	},

	/**
	 * Org-enabled ticket types for Create Ticket (excludes org-disabled types).
	 */
	typesAvailableForCreate: (
		organizationId: string,
		subOrganizationId?: string,
	) =>
		ticketTypesAvailableForCreateQueryOptions(
			organizationId,
			subOrganizationId,
		),

	/**
	 * Ticket types count — GET /tickets/types/count
	 */
	typesCount: (options?: TicketTypesQueryOptionsInput) => {
		const params = normalizeTicketTypesQueryOptions(options);
		return queryOptions({
			queryKey: ticketKeys.ticketTypesCount(params),
			queryFn: () => ticketService.getTicketTypesCount(options),
			staleTime: 0,
			gcTime: 0,
		});
	},

	/**
	 * Ticket types page — GET /tickets/types/{start}/{count}
	 */
	typesPage: (
		start: number,
		count: number,
		options?: TicketTypesQueryOptionsInput,
	) => {
		const params = normalizeTicketTypesQueryOptions(options);
		return queryOptions({
			queryKey: ticketKeys.ticketTypesPage(start, count, params),
			queryFn: () =>
				ticketService.getTicketTypesPaged(options, { start, count }),
			staleTime: 0,
			gcTime: 0,
		});
	},

	/**
	 * All ticket types for org/filter (client search) — GET /tickets/types/{1}/{total}
	 */
	typesAll: (options?: TicketTypesQueryOptionsInput) =>
		ticketTypesAllQueryOptions(options),

	/**
	 * Single ticket type (detail)
	 */
	ticketTypeDetail: (
		ticketTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	) =>
		queryOptions({
			queryKey: ticketKeys.ticketTypeDetail(
				ticketTypeId,
				options?.subOrganizationId,
				options?.organizationId,
			),
			queryFn: () => ticketService.getTicketTypeById(ticketTypeId, options),
			staleTime: 5 * 60 * 1000,
			gcTime: 15 * 60 * 1000,
			enabled: !!ticketTypeId && ticketTypeId.length > 0,
		}),

	/**
	 * Get ticket priorities (for dropdowns, etc.)
	 */
	priorities: (options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: ticketKeys.ticketsPriorities(options?.organizationId),
			queryFn: () => ticketService.getTicketPriorities(options),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
		}),

	/**
	 * Geometry sources — GET /tickets/geometry-sources
	 */
	geometrySources: () =>
		queryOptions({
			queryKey: ticketKeys.geometrySources(),
			queryFn: () => ticketService.getGeometrySources(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * GPS methods — GET /tickets/gps-methods
	 */
	gpsMethods: () =>
		queryOptions({
			queryKey: ticketKeys.gpsMethods(),
			queryFn: () => ticketService.getGpsMethods(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * Get tickets as GeoJSON features
	 */
	features: (start: number = 1, count: number = 10, params?: TicketParams) =>
		queryOptions({
			queryKey: ticketKeys.ticketsFeatures(start, count, params),
			queryFn: () => ticketService.getTicketsFeatures(start, count, params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	// ==================== TICKET ASSIGNMENTS ====================

	assignments: {
		/**
		 * Get ticket assignments count
		 */
		count: (ticketId: string, params?: TicketAssignmentParams) =>
			queryOptions({
				queryKey: ticketKeys.ticketAssignmentsCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketAssignmentsCount(ticketId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Get ticket assignments with pagination
		 */
		list: (
			ticketId: string,
			start: number = 1,
			count: number = 10,
			params?: TicketAssignmentParams,
			organizationId?: string,
		) =>
			queryOptions({
				queryKey: [
					...ticketKeys.ticketAssignmentsList(ticketId, start, count, params),
					organizationId,
				],
				queryFn: () =>
					ticketService.getTicketAssignments(
						ticketId,
						start,
						count,
						params,
						organizationId,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Infinite query for ticket assignments
		 */
		infinite: (
			ticketId: string,
			params?: TicketAssignmentParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...ticketKeys.ticketAssignmentsList(ticketId, 1, pageSize, params),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					ticketService.getTicketAssignments(
						ticketId,
						pageParam,
						pageSize,
						params,
					),
				initialPageParam: 1,
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					if (lastPage.length < pageSize) {
						return undefined;
					}

					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),
	},

	// ==================== TICKET SUB-CONTRACTORS ====================

	subContractors: {
		/**
		 * Get ticket sub-contractors count
		 */
		count: (ticketId: string, params?: SubContractorParams) =>
			queryOptions({
				queryKey: ticketKeys.ticketSubContractorsCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketSubContractorsCount(ticketId, params),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Get ticket sub-contractors with pagination
		 */
		list: (
			ticketId: string,
			start: number = 1,
			count: number = 10,
			params?: SubContractorParams,
			organizationId?: string,
		) =>
			queryOptions({
				queryKey: [
					...ticketKeys.ticketSubContractorsList(
						ticketId,
						start,
						count,
						params,
					),
					organizationId,
				],
				queryFn: () =>
					ticketService.getTicketSubContractors(
						ticketId,
						start,
						count,
						params,
						organizationId,
					),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Infinite query for ticket sub-contractors
		 */
		infinite: (
			ticketId: string,
			params?: SubContractorParams,
			pageSize: number = 10,
			totalCount?: number,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...ticketKeys.ticketSubContractorsList(ticketId, 1, pageSize, params),
					"infinite",
				],
				queryFn: ({ pageParam }) =>
					ticketService.getTicketSubContractors(
						ticketId,
						pageParam,
						pageSize,
						params,
					),
				initialPageParam: 1,
				getNextPageParam: (lastPage, allPages, lastPageParam) => {
					if (totalCount !== undefined) {
						const fetched = allPages.reduce(
							(acc, page) => acc + page.length,
							0,
						);
						if (fetched >= totalCount) {
							return undefined;
						}
					}

					if (lastPage.length < pageSize) {
						return undefined;
					}

					return lastPageParam + pageSize;
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (firstPageParam <= 1) {
						return undefined;
					}
					return Math.max(1, firstPageParam - pageSize);
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),
	},

	// ==================== TICKET TYPE HISTORY ====================

	ticketTypeHistory: {
		count: (ticketTypeId: string, organizationId?: string) =>
			queryOptions({
				queryKey: [
					...ticketKeys.ticketTypeHistoryCount(ticketTypeId),
					organizationId,
				],
				queryFn: () =>
					ticketService.getTicketTypeHistoryCount(ticketTypeId, organizationId),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!ticketTypeId && ticketTypeId.length > 0,
			}),

		infinite: (
			ticketTypeId: string,
			pageSize: number = 10,
			totalCount: number,
			organizationId?: string,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...ticketKeys.ticketTypeHistoryList(ticketTypeId, 1, pageSize),
					"infinite",
					totalCount,
					organizationId,
				],
				queryFn: ({ pageParam }) =>
					ticketService.getTicketTypeHistory(
						ticketTypeId,
						pageParam,
						pageSize,
						organizationId,
					),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled:
					!!ticketTypeId && ticketTypeId.length > 0 && totalCount !== undefined,
			}),
	},

	// ==================== TICKET HISTORY ====================

	history: {
		/**
		 * Get ticket history count
		 */
		count: (ticketId: string) =>
			queryOptions({
				queryKey: ticketKeys.ticketHistoryCount(ticketId),
				queryFn: () => ticketService.getTicketHistoryCount(ticketId),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Get ticket history with pagination
		 */
		list: (ticketId: string, start: number = 1, count: number = 10) =>
			queryOptions({
				queryKey: ticketKeys.ticketHistoryList(ticketId, start, count),
				queryFn: () => ticketService.getTicketHistory(ticketId, start, count),
				staleTime: 5 * 60 * 1000, // 5 minutes
				gcTime: 10 * 60 * 1000, // 10 minutes
				enabled: !!ticketId && ticketId.length > 0,
			}),

		/**
		 * Infinite query for ticket history (newest first)
		 */
		infinite: (ticketId: string, pageSize: number = 10, totalCount: number) =>
			infiniteQueryOptions({
				queryKey: [
					...ticketKeys.ticketHistoryList(ticketId, 1, pageSize),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					ticketService.getTicketHistory(ticketId, pageParam, pageSize),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) {
						return undefined;
					}

					const nextStart = lastPageParam - pageSize;
					return Math.max(1, nextStart);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0 && totalCount !== undefined,
			}),
	},

	// ==================== TICKET ASSET ASSOCIATIONS ====================

	assets: {
		count: (
			ticketId: string,
			params?: TicketAssetAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketAssetsCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketAssetsCount(ticketId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),

		list: (
			ticketId: string,
			start: number = 1,
			count: number = 50,
			params?: TicketAssetAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketAssetsList(ticketId, start, count, params),
				queryFn: () =>
					ticketService.getTicketAssets(
						ticketId,
						start,
						count,
						params,
						options,
					),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),
	},

	// ==================== TICKET RECORD ASSOCIATIONS ====================

	records: {
		count: (
			ticketId: string,
			params?: TicketRecordAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketRecordsCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketRecordsCount(ticketId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),

		list: (
			ticketId: string,
			start: number = 1,
			count: number = 50,
			params?: TicketRecordAssociationParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketRecordsList(ticketId, start, count, params),
				queryFn: () =>
					ticketService.getTicketRecords(
						ticketId,
						start,
						count,
						params,
						options,
					),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),
	},

	// ==================== TICKET FILES ====================

	files: {
		count: (
			ticketId: string,
			params?: TicketFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketFilesCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketFilesCount(ticketId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),

		list: (
			ticketId: string,
			start: number = 1,
			count: number = 50,
			params?: TicketFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketFilesList(ticketId, start, count, params),
				queryFn: () =>
					ticketService.getTicketFiles(ticketId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),

		content: (
			ticketId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketFileContent(ticketId, fileId),
				queryFn: () => ticketService.getTicketFile(ticketId, fileId, options),
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!ticketId && !!fileId,
			}),

		thumbnail: (
			ticketId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketFileThumbnail(ticketId, fileId),
				queryFn: async () => {
					try {
						return await ticketService.getTicketFileThumbnail(
							ticketId,
							fileId,
							options,
						);
					} catch {
						return null;
					}
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!ticketId && !!fileId,
				retry: false,
			}),
	},

	// ==================== TICKET NOTES ====================

	notes: {
		count: (
			ticketId: string,
			params?: NoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketNotesCount(ticketId, params),
				queryFn: () =>
					ticketService.getTicketNotesCount(ticketId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),

		list: (
			ticketId: string,
			start: number = 1,
			count: number = 50,
			params?: NoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: ticketKeys.ticketNotesList(ticketId, start, count, params),
				queryFn: () =>
					ticketService.getTicketNotes(ticketId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!ticketId && ticketId.length > 0,
			}),
	},
};

// ==================== CONVENIENCE EXPORTS ====================

/**
 * All ticket query options grouped by category
 */
export const ticketQueries = {
	tickets: ticketQueryOptions,
	assignments: ticketQueryOptions.assignments,
	subContractors: ticketQueryOptions.subContractors,
	history: ticketQueryOptions.history,
	assets: ticketQueryOptions.assets,
	records: ticketQueryOptions.records,
	files: ticketQueryOptions.files,
	notes: ticketQueryOptions.notes,
} as const;

/**
 * Query key factory for easy invalidation
 */
export const ticketQueryKeys = ticketKeys;
